import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { Company } from 'types/company.types';

interface Props {
  open: boolean;
  loading: boolean;
  tenant: Company | null;

  name: string;
  addressLine1: string;
  postalCode: string;
  city: string;

  handleTenantInfoChange(field: 'name' | 'addressLine1' | 'postalCode' | 'city'): (event: React.ChangeEvent<HTMLInputElement>) => void;

  createTenant(): void;

  updateTenant(): void;

  handleClose(): void;
}

const CreateAccountDialog: React.FC<Props> = ({
  open,
  loading,
  tenant,
  name,
  addressLine1,
  postalCode,
  city,
  handleTenantInfoChange,
  createTenant,
  updateTenant,
  handleClose,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} fullWidth={true} maxWidth={'xs'}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{tenant ? t('editTenant') : t('createTenant')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack direction={'column'} gap={2}>
          <PaiTextField label={t('name')} placeholder={t('enterTenantNameHere')} value={name} onChange={handleTenantInfoChange('name')} />
          <PaiTextField
            label={t('street')}
            placeholder={t('enterStreetHere')}
            value={addressLine1}
            onChange={handleTenantInfoChange('addressLine1')}
          />
          <PaiTextField
            label={t('postalCode')}
            placeholder={t('enterPostalCodeHere')}
            value={postalCode}
            onChange={handleTenantInfoChange('postalCode')}
          />
          <PaiTextField label={t('city')} placeholder={t('enterCityHere')} value={city} onChange={handleTenantInfoChange('city')} />
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'md'} underline={'always'} action={handleClose} />
        <PaiButton
          text={tenant ? t('save') : t('create')}
          border={'rounded'}
          size={'small'}
          action={tenant ? updateTenant : createTenant}
          disabled={name.trim() === '' || addressLine1.trim() === '' || postalCode.trim() === '' || city.trim() === ''}
          loading={loading}
        />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default CreateAccountDialog;
